import { inputClass } from '../utils/formStyles';
import { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiAlertTriangle } from 'react-icons/fi';
import { teamService } from '../services/teamService';
import { Member } from '../types';
import Avatar from './Avatar';
import Modal from './Modal';

interface StageAssigneePickerProps {
  /** The team that owns the stage being handed to. */
  teamId?: string;
  teamName?: string;
  stageName?: string;
  value: string;
  onChange: (memberId: string) => void;
  /** Raised so callers can block their submit while the team is unstaffed. */
  onBlockedChange?: (blocked: boolean) => void;
  disabled?: boolean;
}

export const AUTO_ASSIGN = '';
const MANUAL = '__manual__';

const memberName = (member: Member) => `${member.firstName} ${member.lastName}`.trim();

const StageAssigneePicker = ({
  teamId,
  teamName,
  stageName,
  value,
  onChange,
  onBlockedChange,
  disabled = false,
}: StageAssigneePickerProps) => {
  const selectId = useId();
  const [members, setMembers] = useState<Member[] | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!teamId) {
      setMembers(null);
      return;
    }
    teamService
      .getMembers(teamId)
      .then((rows) => {
        if (!cancelled) setMembers(rows);
      })
      .catch(() => {
        if (!cancelled) setMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  // An empty team means the handover would strand the work, so callers are told
  // to block rather than discovering it after the fact.
  const blocked = members !== null && members.length === 0;
  useEffect(() => {
    onBlockedChange?.(blocked);
  }, [blocked, onBlockedChange]);

  if (!teamId) return null;

  if (blocked) {
    return (
      <div role="alert" className="flex items-start gap-2.5 px-3 py-2.5 bg-danger-subtle border border-danger-border rounded-control text-body text-danger">
        <FiAlertTriangle aria-hidden="true" className="mt-0.5 shrink-0" />
        <span>
          <strong>{teamName ?? 'That team'}</strong> has no members, so{' '}
          {stageName ? <strong>{stageName}</strong> : 'the next stage'} has nobody to receive this.
          Add someone to the team first.
        </span>
      </div>
    );
  }

  const chosen = (members ?? []).find((member) => member.memberId === value);
  const manual = open || Boolean(chosen);

  return (
    <div>
      <label htmlFor={selectId} className="block text-body font-medium text-ink mb-1.5">
        Hand over to {stageName ? <span className="font-normal text-ink-subtle">· {stageName}</span> : null}
      </label>
      <select
        id={selectId}
        value={manual ? MANUAL : AUTO_ASSIGN}
        disabled={disabled || members === null}
        onChange={(e) => {
          if (e.target.value === AUTO_ASSIGN) {
            setOpen(false);
            onChange(AUTO_ASSIGN);
            return;
          }
          setOpen(true);
        }}
        className={inputClass}
      >
        <option value={AUTO_ASSIGN}>Auto — least loaded member of {teamName ?? 'the team'}</option>
        <option value={MANUAL}>Manual selection</option>
      </select>
      {chosen && (
        <p className="mt-1.5 text-meta text-ink-muted">
          {memberName(chosen)}
          {!disabled && (
            <button type="button" className="ml-2 text-primary font-medium" onClick={() => setOpen(true)}>
              Change
            </button>
          )}
        </p>
      )}
      {typeof document !== 'undefined' &&
        createPortal(
          <Modal
            isOpen={open}
            title="Choose a member"
            description={
              stageName
                ? `Who should take ${stageName}?`
                : `Members of ${teamName ?? 'the team'}.`
            }
            size="sm"
            onClose={() => setOpen(false)}
          >
            <ul className="space-y-1">
              {(members ?? []).map((member) => (
                <li key={member.memberId}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(member.memberId);
                      setOpen(false);
                    }}
                    className={`flex w-full items-center gap-3 rounded-control px-3 py-2.5 text-left text-body text-ink hover:bg-surface-sunken ${
                      member.memberId === value ? 'bg-primary-subtle' : ''
                    }`}
                  >
                    <Avatar name={memberName(member)} />
                    {memberName(member)}
                  </button>
                </li>
              ))}
            </ul>
          </Modal>,
          document.body
        )}
    </div>
  );
};

export default StageAssigneePicker;
